import * as Sentry from "@sentry/nextjs";
import { getAuthSessionId } from "@/infrastructure/auth/AuthSessionManager";
import {
  isAbortError,
  isTransportError,
  type TransportErrorCode,
} from "@/infrastructure/http/transport-error";
import { trackAmplitudeEvent } from "@/lib/analytics/amplitude";

export type AuthFlow =
  | "login"
  | "sign_up"
  | "session_restore"
  | "token_refresh"
  | "authenticated_api";

export type AuthMethod = "email_password" | "google_oauth" | "unknown";

export type AuthStage =
  | "firebase_sign_in"
  | "firebase_popup"
  | "profile_ensure"
  | "identitytoolkit_proxy"
  | "securetoken_proxy"
  | "token_refresh"
  | "auth_header_build"
  | "id_token_listener"
  | "auth_cleanup_redirect";

export type FailureCategory =
  | "network"
  | "offline"
  | "http_4xx"
  | "http_5xx"
  | "firebase_auth"
  | "popup_blocked"
  | "popup_closed"
  | "cancelled"
  | "unknown";

type VisibilityStateValue =
  | "hidden"
  | "visible"
  | "prerender"
  | "unknown";

type NavigationTypeValue =
  | "navigate"
  | "reload"
  | "back_forward"
  | "prerender"
  | "unknown";

type AttemptStoreEntry = {
  lastFailed: boolean;
  retryIndex: number;
};

const ATTEMPT_STORAGE_KEY = "aseed-auth-attempt-state-v1";
const AUTH_TELEMETRY_SIGNAL = "auth_transport_failure";
const SILENT_SENTRY_CATEGORIES = new Set<FailureCategory>([
  "cancelled",
  "popup_closed",
]);

export interface AuthAttempt {
  attemptId: string;
  authFlow: "login" | "sign_up";
  authMethod: AuthMethod;
  entryPoint?: string;
  retryIndex: number;
}

export interface AuthFailureAnalyticsProps {
  auth_flow: AuthFlow;
  auth_method: AuthMethod;
  auth_stage: AuthStage;
  failure_category: FailureCategory;
  transport_code: TransportErrorCode;
  http_status?: number;
  request_target?: string;
  is_online?: boolean;
  visibility_state: VisibilityStateValue;
  navigation_type: NavigationTypeValue;
  auth_session_id: number;
  firebase_error_code?: string;
  attempt_id?: string;
  retry_index?: number;
  entry_point?: string;
  recovered_after_failure?: boolean;
}

export interface CaptureAuthFailureTelemetryInput {
  authFlow: AuthFlow;
  authMethod?: AuthMethod;
  authStage: AuthStage;
  error?: unknown;
  httpStatus?: number;
  requestTarget?: string | URL | null;
  failureCategory?: FailureCategory;
  transportCode?: TransportErrorCode;
  firebaseErrorCode?: string;
  attemptId?: string;
  retryIndex?: number;
  entryPoint?: string;
  extra?: Record<string, unknown>;
}

function isClientSide(): boolean {
  return typeof window !== "undefined";
}

function createAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2, 10);
  return `auth_${Date.now()}_${randomPart}`;
}

function getAttemptStore(): Record<string, AttemptStoreEntry> {
  if (!isClientSide()) {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(ATTEMPT_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, AttemptStoreEntry>)
      : {};
  } catch {
    return {};
  }
}

function setAttemptStore(store: Record<string, AttemptStoreEntry>): void {
  if (!isClientSide()) {
    return;
  }

  try {
    window.sessionStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore storage failures in privacy-restricted environments.
  }
}

function buildAttemptKey(input: {
  authFlow: "login" | "sign_up";
  authMethod: AuthMethod;
  entryPoint?: string;
}): string {
  return [
    input.authFlow,
    input.authMethod,
    input.entryPoint ?? "default",
  ].join(":");
}

function toErrorLike(
  error: unknown
): { code?: unknown; message?: unknown; response?: { status?: unknown } } {
  return typeof error === "object" && error !== null
    ? (error as {
        code?: unknown;
        message?: unknown;
        response?: { status?: unknown };
      })
    : {};
}

export function extractFirebaseErrorCode(error: unknown): string | undefined {
  const maybeError = toErrorLike(error);
  return typeof maybeError.code === "string" ? maybeError.code : undefined;
}

export function extractHttpStatus(error: unknown): number | undefined {
  if (isTransportError(error) && typeof error.status === "number") {
    return error.status;
  }

  const maybeStatus = toErrorLike(error).response?.status;
  return typeof maybeStatus === "number" ? maybeStatus : undefined;
}

export function sanitizeAuthRequestTarget(
  requestTarget?: string | URL | null
): string | undefined {
  if (!requestTarget) {
    return undefined;
  }

  try {
    const resolvedUrl =
      requestTarget instanceof URL
        ? requestTarget
        : new URL(requestTarget, "https://aseed.invalid");

    const segments = resolvedUrl.pathname
      .split("/")
      .map((segment) => segment.trim())
      .filter(Boolean);
    const versionIndex = segments.findIndex((segment) => /^v\d+$/i.test(segment));
    const rawTarget =
      versionIndex >= 0
        ? segments[versionIndex + 1] ?? segments[segments.length - 1]
        : segments[segments.length - 1];

    if (!rawTarget) {
      return undefined;
    }

    return decodeURIComponent(rawTarget)
      .replace(/[^A-Za-z0-9:_-]/g, "")
      .slice(0, 120);
  } catch {
    return undefined;
  }
}

function extractTransportCode(
  error: unknown,
  explicitStatus?: number,
  explicitTransportCode?: TransportErrorCode
): TransportErrorCode {
  if (explicitTransportCode) {
    return explicitTransportCode;
  }

  if (isTransportError(error)) {
    return error.code;
  }

  if (isAbortError(error)) {
    return "REQUEST_ABORTED";
  }

  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error ?? "");

  if (message.includes("timeout")) {
    return "TIMEOUT";
  }

  if (typeof explicitStatus === "number") {
    return "HTTP_ERROR";
  }

  if (error instanceof TypeError) {
    return "NETWORK_ERROR";
  }

  return "UNKNOWN";
}

function getIsOnline(): boolean | undefined {
  if (!isClientSide() || typeof navigator.onLine !== "boolean") {
    return undefined;
  }

  return navigator.onLine;
}

function getVisibilityState(): VisibilityStateValue {
  if (typeof document === "undefined") {
    return "unknown";
  }

  const visibilityState = document.visibilityState;
  return visibilityState === "hidden" ||
    visibilityState === "visible" ||
    visibilityState === "prerender"
    ? visibilityState
    : "unknown";
}

function getNavigationType(): NavigationTypeValue {
  if (typeof performance === "undefined") {
    return "unknown";
  }

  const entry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  const type = entry?.type;
  return type === "navigate" ||
    type === "reload" ||
    type === "back_forward" ||
    type === "prerender"
    ? type
    : "unknown";
}

function normalizeFailureCategory(input: {
  error?: unknown;
  httpStatus?: number;
  firebaseErrorCode?: string;
  transportCode: TransportErrorCode;
  explicitCategory?: FailureCategory;
  isOnline?: boolean;
}): FailureCategory {
  if (input.explicitCategory) {
    return input.explicitCategory;
  }

  if (typeof input.httpStatus === "number") {
    if (input.httpStatus >= 500) {
      return "http_5xx";
    }
    if (input.httpStatus >= 400) {
      return "http_4xx";
    }
  }

  const firebaseErrorCode = input.firebaseErrorCode?.toLowerCase();
  if (firebaseErrorCode) {
    if (firebaseErrorCode === "auth/popup-blocked") {
      return "popup_blocked";
    }
    if (firebaseErrorCode === "auth/popup-closed-by-user") {
      return "popup_closed";
    }
    if (firebaseErrorCode === "auth/cancelled-popup-request") {
      return "cancelled";
    }
    if (firebaseErrorCode === "auth/network-request-failed") {
      return input.isOnline === false ? "offline" : "network";
    }
    if (firebaseErrorCode.startsWith("auth/")) {
      return "firebase_auth";
    }
  }

  if (input.transportCode === "REQUEST_ABORTED") {
    return "cancelled";
  }

  if (
    input.transportCode === "NETWORK_ERROR" ||
    input.transportCode === "TIMEOUT"
  ) {
    return input.isOnline === false ? "offline" : "network";
  }

  return "unknown";
}

export function buildAuthFailureAnalyticsProps(
  input: CaptureAuthFailureTelemetryInput
): AuthFailureAnalyticsProps {
  const httpStatus = input.httpStatus ?? extractHttpStatus(input.error);
  const firebaseErrorCode =
    input.firebaseErrorCode ?? extractFirebaseErrorCode(input.error);
  const isOnline = getIsOnline();
  const transportCode = extractTransportCode(
    input.error,
    httpStatus,
    input.transportCode
  );
  const failureCategory = normalizeFailureCategory({
    error: input.error,
    httpStatus,
    firebaseErrorCode,
    transportCode,
    explicitCategory: input.failureCategory,
    isOnline,
  });

  return {
    auth_flow: input.authFlow,
    auth_method: input.authMethod ?? "unknown",
    auth_stage: input.authStage,
    failure_category: failureCategory,
    transport_code: transportCode,
    http_status: httpStatus,
    request_target: sanitizeAuthRequestTarget(input.requestTarget),
    is_online: isOnline,
    visibility_state: getVisibilityState(),
    navigation_type: getNavigationType(),
    auth_session_id: getAuthSessionId(),
    firebase_error_code: firebaseErrorCode,
    attempt_id: input.attemptId,
    retry_index: input.retryIndex,
    entry_point: input.entryPoint,
  };
}

export function shouldCaptureAuthTransportTelemetry(
  props: Pick<AuthFailureAnalyticsProps, "failure_category">
): boolean {
  return props.failure_category !== "firebase_auth";
}

function toContextRecord(
  payload: AuthFailureAnalyticsProps,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  return {
    ...payload,
    ...(extra ?? {}),
  };
}

export function captureAuthFailureTelemetry(
  input: CaptureAuthFailureTelemetryInput
): AuthFailureAnalyticsProps {
  const payload = buildAuthFailureAnalyticsProps(input);

  trackAmplitudeEvent(
    "result_auth_transport_failed_client",
    toContextRecord(payload)
  );

  if (SILENT_SENTRY_CATEGORIES.has(payload.failure_category)) {
    return payload;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(
      payload.failure_category === "http_5xx" ||
        payload.failure_category === "offline" ||
        payload.failure_category === "network"
        ? "error"
        : "warning"
    );
    scope.setTag("telemetry_signal", AUTH_TELEMETRY_SIGNAL);
    scope.setTag("auth_flow", payload.auth_flow);
    scope.setTag("auth_stage", payload.auth_stage);
    scope.setTag("failure_category", payload.failure_category);
    scope.setFingerprint([
      AUTH_TELEMETRY_SIGNAL,
      payload.auth_flow,
      payload.auth_stage,
      payload.failure_category,
      payload.request_target ?? "unknown",
    ]);
    scope.setContext("auth_failure", toContextRecord(payload, input.extra));
    Sentry.captureMessage(AUTH_TELEMETRY_SIGNAL);
  });

  return payload;
}

export function addAuthTelemetryBreadcrumb(input: {
  message: string;
  data?: Record<string, unknown>;
}): void {
  Sentry.addBreadcrumb({
    category: "auth.telemetry",
    level: "info",
    message: input.message,
    data: input.data,
  });
}

export function beginAuthAttempt(input: {
  authFlow: "login" | "sign_up";
  authMethod: AuthMethod;
  entryPoint?: string;
}): AuthAttempt {
  const key = buildAttemptKey(input);
  const store = getAttemptStore();
  const previousState = store[key];
  const retryIndex = previousState?.lastFailed ? previousState.retryIndex + 1 : 0;
  const attempt: AuthAttempt = {
    attemptId: createAttemptId(),
    authFlow: input.authFlow,
    authMethod: input.authMethod,
    entryPoint: input.entryPoint,
    retryIndex,
  };

  store[key] = {
    // Keep the preceding failure until success consumes the recovery marker.
    lastFailed: previousState?.lastFailed ?? false,
    retryIndex,
  };
  setAttemptStore(store);

  return attempt;
}

export function recordAuthAttemptFailure(attempt: AuthAttempt): void {
  const key = buildAttemptKey(attempt);
  const store = getAttemptStore();
  store[key] = {
    lastFailed: true,
    retryIndex: attempt.retryIndex,
  };
  setAttemptStore(store);
}

export function consumeAuthRecoveryState(attempt: AuthAttempt): boolean {
  const key = buildAttemptKey(attempt);
  const store = getAttemptStore();
  const previousState = store[key];
  const recovered = Boolean(previousState?.lastFailed);
  delete store[key];
  setAttemptStore(store);
  return recovered;
}

export function clearAuthAttemptState(attempt: AuthAttempt): void {
  const key = buildAttemptKey(attempt);
  const store = getAttemptStore();
  delete store[key];
  setAttemptStore(store);
}

export function resolveProxyAuthStage(hostname: string): AuthStage {
  return hostname === "securetoken.googleapis.com"
    ? "securetoken_proxy"
    : "identitytoolkit_proxy";
}

export function resolveProxyAuthFlow(requestTarget?: string): AuthFlow {
  if (requestTarget === "token") {
    return "token_refresh";
  }

  if (!isClientSide()) {
    return "session_restore";
  }

  const pathname = (window.location.pathname ?? "").toLowerCase();
  if (pathname === "/signup" || pathname.startsWith("/signup/")) {
    return "sign_up";
  }
  if (pathname === "/login" || pathname.startsWith("/login/")) {
    return "login";
  }
  return "session_restore";
}

export function resolveProxyAuthMethod(requestTarget?: string): AuthMethod {
  if (requestTarget === "accounts:signInWithPassword") {
    return "email_password";
  }
  if (requestTarget === "accounts:signInWithIdp") {
    return "google_oauth";
  }
  return "unknown";
}
